import { type Ref, readonly, ref } from "vue";
import { i18n } from "@/shared/i18n";

/**
 * What the product says back when you do something.
 *
 * Until now it said nothing: 18 files kept an `error = ref` of their own and
 * painted it wherever there happened to be room, and a save that worked printed a
 * "salvo" that half the screens had invented and half had not. One queue, one
 * viewport, one voice.
 *
 * Module-level state, like `use-nav-collapse`: the viewport is mounted once in the
 * shell, and the code that reports an outcome is a network callback deep inside a
 * feature, with no component instance to inject through. Deliberately NOT
 * persisted — a message about what just happened has no business surviving a
 * reload.
 */
export type ToastTone = "success" | "error" | "info";

export interface ToastItem {
	readonly id: number;
	readonly tone: ToastTone;
	readonly message: string;
	/** Um gesto ao lado da frase ("Editar"): clicar corre e fecha o aviso. */
	readonly action?: ToastAction;
}

export interface ToastAction {
	readonly label: string;
	readonly run: () => void;
}

/** Long enough to read, short enough not to sit on the button you want next. */
const LIFETIME: Record<ToastTone, number> = { success: 4000, error: 8000, info: 5000 };

/** A failing loop must not paper over the screen; the oldest gives way. */
const MAX_VISIBLE = 4;

const items = ref<ToastItem[]>([]);
let sequence = 0;

function dismiss(id: number): void {
	items.value = items.value.filter((item) => item.id !== id);
}

function push(tone: ToastTone, message: string, action?: ToastAction): number {
	const id = ++sequence;
	items.value = [...items.value, { id, tone, message, ...(action ? { action } : {}) }].slice(
		-MAX_VISIBLE,
	);
	setTimeout(() => dismiss(id), LIFETIME[tone]);
	return id;
}

/**
 * Callers hold a `catch (e: unknown)`, not a string. Asking each of them to unwrap
 * it is how you get 18 spellings of the same line back again.
 */
function messageOf(cause: unknown): string {
	if (typeof cause === "string" && cause.trim()) return cause;
	if (cause instanceof Error && cause.message) return cause.message;
	// A última frase do painel que só existia em português — e a que MAIS aparece,
	// porque é o que um toast diz quando o erro não trouxe mensagem nenhuma.
	// `i18n.global` e não `useI18n`: isto é uma função pura chamada de dentro de um
	// `catch`, longe de qualquer `setup`.
	return i18n.global.t("error.generic");
}

export const toast = {
	success: (message: string) => push("success", message),
	info: (message: string) => push("info", message),
	/** Takes the message OR the thrown thing. */
	error: (cause: unknown, action?: ToastAction) => push("error", messageOf(cause), action),
	dismiss,
};

export function useToast(): {
	toasts: Readonly<Ref<readonly ToastItem[]>>;
	dismiss: (id: number) => void;
} {
	return { toasts: readonly(items), dismiss };
}
